import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Vérifier si l'app est déjà installée
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Montrer la bannière après un délai
      setTimeout(() => setShowInstallBanner(true), 3000);
    };

    // Écouter si l'app est installée
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Afficher le prompt d'installation
    deferredPrompt.prompt();
    
    // Attendre le choix de l'utilisateur
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('Hypnose clé installé!');
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    // Ne plus montrer pendant cette session
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  // Ne rien afficher si déjà installé ou bannière déjà fermée
  if (isInstalled || !showInstallBanner || sessionStorage.getItem('pwa-banner-dismissed')) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
      data-testid="pwa-install-banner"
    >
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header avec bouton fermer */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-purple-500 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Installer Hypnose clé</h3>
              <p className="text-white/60 text-sm">Accès rapide depuis votre écran</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/40 hover:text-white/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Contenu */}
        <div className="p-4">
          <ul className="text-white/70 text-sm space-y-2 mb-4">
            <li className="flex items-center gap-2">
              <span className="text-amber-400">✓</span> Accès hors-ligne
            </li>
            <li className="flex items-center gap-2">
              <span className="text-amber-400">✓</span> Lancement rapide
            </li>
            <li className="flex items-center gap-2">
              <span className="text-amber-400">✓</span> Expérience plein écran
            </li>
          </ul>
          
          <button
            onClick={handleInstallClick}
            className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-purple-500 hover:from-amber-400 hover:to-purple-400 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
          >
            <Download className="w-5 h-5" />
            Installer l'application
          </button>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default InstallPWA;
